import React, { Component } from "react";
import AuthRequiredModal from "../components/common/AuthRequiredModal";
import ServerErrorToast from "../components/common/ServerErrorToast";
import LocalDraftToast from "../components/common/LocalDraftToast";
import { AUTH_REQUIRED_EVENT, resetAuthRequired } from "../utils/authRequired";

// Always-mounted root, guaranteeing the editor never renders blank:
//  1. Error boundary — any render crash below shows AuthRequiredModal
//     instead of a white screen.
//  2. Listens for AUTH_REQUIRED_EVENT so a runtime 401/403 (dispatched from
//     anywhere, e.g. http_service.es6) overlays the popup on an already
//     running editor without unmounting it.
//  3. Starts already-showing when index.jsx boots with no token
//     (initialAuthRequired), in which case `children` is null and only the
//     popup renders.
export default class AppErrorBoundary extends Component {
  state = {
    hasError: false,
    crashError: null,
    authRequired: !!this.props.initialAuthRequired,
    authReason: null,
  };

  static getDerivedStateFromError(error) {
    return { hasError: true, crashError: error };
  }

  componentDidCatch(error, info) {
    console.error("Editor render crashed:", error, info);
  }

  componentDidMount() {
    window.addEventListener(AUTH_REQUIRED_EVENT, this.handleAuthRequired);
  }

  componentWillUnmount() {
    window.removeEventListener(AUTH_REQUIRED_EVENT, this.handleAuthRequired);
  }

  handleAuthRequired = (event) =>
    this.setState({ authRequired: true, authReason: event?.detail?.reason || null });

  handleDismiss = () => {
    resetAuthRequired();
    this.setState({ authRequired: false, authReason: null });
  };

  render() {
    if (this.state.hasError) {
      // A render crash is NOT an auth problem — show the crash screen with
      // the real error so it can't masquerade as "session expired".
      return (
        <AuthRequiredModal
          reason="crash"
          errorText={String(this.state.crashError?.message || this.state.crashError || "")}
        />
      );
    }
    return (
      <>
        {this.props.children}
        {this.state.authRequired && (
          <AuthRequiredModal
            reason={this.state.authReason}
            onDismiss={this.props.children ? this.handleDismiss : undefined}
          />
        )}
        <ServerErrorToast />
        <LocalDraftToast />
      </>
    );
  }
}
